import { useQuery } from '@tanstack/react-query';

import { v1Api } from '@/api/backendApi';
import { systemKeys } from '@/utils/queryKeys';

export interface CurrentPriceResponse {
  amount: number;
  currency: string;
  frequency: 'year' | 'month';
  nextBillingDate: string;
  plan: string;
}

interface UseCurrentPriceOptions {
  enabled?: boolean;
}

export const useCurrentPrice = ({
  enabled = true,
}: UseCurrentPriceOptions = {}) =>
  useQuery({
    queryKey: systemKeys.currentPrice(),
    queryFn: () =>
      v1Api.get<CurrentPriceResponse>('v2/subscription/account/user-price', {
        withCredentials: true,
        headers: {
          'X-Skip-CDN': 'true',
        },
      }),
    enabled,
  });

/**
 * Billing period as a tri-state. `frequency` is `null` until the query
 * resolves, and callers must branch on `isResolved` rather than treating an
 * absent frequency as monthly: defaulting a yearly subscriber to monthly
 * silently rebills them on the wrong period.
 */
export const useBillingPeriod = ({
  enabled = true,
}: UseCurrentPriceOptions = {}) => {
  const { data, isPending, isError, refetch } = useCurrentPrice({ enabled });
  const price = data?.data;
  const frequency = price?.frequency ?? null;

  return {
    price,
    frequency,
    isYearly: frequency === 'year',
    isMonthly: frequency === 'month',
    isResolved: frequency !== null,
    isPending,
    isError,
    refetch,
  };
};
